import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AppLayout from './components/layout/AppLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoadingSpinner from './components/common/LoadingSpinner';

// Lazy Load Pages
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Home = lazy(() => import('./pages/Home'));
const Profile = lazy(() => import('./pages/Profile'));
const StudentDashboard = lazy(() => import('./pages/StudentDashboard'));
const CreatePost = lazy(() => import('./pages/CreatePost'));
const PostFeed = lazy(() => import('./pages/PostFeed'));
const Groups = lazy(() => import('./pages/Groups'));
const GroupDetail = lazy(() => import('./pages/GroupDetail'));
const Classes = lazy(() => import('./pages/Classes'));
const ClassDetail = lazy(() => import('./pages/ClassDetail'));
const SchoolDashboard = lazy(() => import('./pages/SchoolDashboard'));

// Onboarding Pages
const StudentOnboarding = lazy(() => import('./pages/onboarding/StudentOnboarding'));
const TeacherOnboarding = lazy(() => import('./pages/onboarding/TeacherOnboarding'));
const SchoolOnboarding = lazy(() => import('./pages/onboarding/SchoolOnboarding'));

// School ERP Pages
const SchoolERPLayout = lazy(() => import('./components/layouts/SchoolERPLayout'));
const ERPDashboard = lazy(() => import('./pages/school-erp/ERPDashboard'));
const StudentsList = lazy(() => import('./pages/school-erp/students/StudentsList'));
const StudentProfile = lazy(() => import('./pages/school-erp/students/StudentProfile'));
const AdmissionWizard = lazy(() => import('./pages/school-erp/students/AdmissionWizard'));
const BulkImport = lazy(() => import('./pages/school-erp/students/BulkImport'));
const StudentAttendance = lazy(() => import('./pages/school-erp/students/StudentAttendance'));
const FeeManager = lazy(() => import('./pages/school-erp/finance/FeeManager'));
const ExpenseTracker = lazy(() => import('./pages/school-erp/finance/ExpenseTracker'));
const ClassConfig = lazy(() => import('./pages/school-erp/academics/ClassConfig'));
const TimetableBuilder = lazy(() => import('./pages/school-erp/academics/TimetableBuilder'));
const StaffDirectory = lazy(() => import('./pages/school-erp/hr/StaffDirectory'));
const StaffAttendance = lazy(() => import('./pages/school-erp/hr/StaffAttendance'));
const LeaveManagement = lazy(() => import('./pages/school-erp/hr/LeaveManagement'));
const PayrollManager = lazy(() => import('./pages/school-erp/hr/PayrollManager'));
const ExamManager = lazy(() => import('./pages/school-erp/academics/ExamManager'));
const FinanceReports = lazy(() => import('./pages/school-erp/finance/FinanceReports'));

// New ERP Modules
const ReportCards = lazy(() => import('./pages/school-erp/academics/ReportCards'));
const LibraryManager = lazy(() => import('./pages/school-erp/library/LibraryManager'));
const TransportManager = lazy(() => import('./pages/school-erp/transport/TransportManager'));
const HostelManager = lazy(() => import('./pages/school-erp/hostel/HostelManager'));
const InventoryManager = lazy(() => import('./pages/school-erp/inventory/InventoryManager'));
const FrontOffice = lazy(() => import('./pages/school-erp/frontoffice/FrontOffice'));
const CertificateManager = lazy(() => import('./pages/school-erp/certificates/CertificateManager'));
const CommunicationCenter = lazy(() => import('./pages/school-erp/communication/CommunicationCenter'));
const SettingsManager = lazy(() => import('./pages/school-erp/settings/SettingsManager'));
const AnalyticsDashboard = lazy(() => import('./pages/school-erp/analytics/AnalyticsDashboard'));

// Protected Route Component
function ProtectedRoute({ children }) {
  const { currentUser, loading } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (!currentUser) return <Navigate to="/login" />;

  return children;
}

// Public Route (redirect if logged in)
function PublicRoute({ children }) {
  const { currentUser, loading } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (currentUser) return <Navigate to="/" />;

  return children;
}

function AppRoutes() {

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute><Login /></PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute><Register /></PublicRoute>
      } />

      {/* Onboarding Routes */}
      <Route path="/onboarding/student" element={
        <ProtectedRoute><StudentOnboarding /></ProtectedRoute>
      } />
      <Route path="/onboarding/teacher" element={
        <ProtectedRoute><TeacherOnboarding /></ProtectedRoute>
      } />
      <Route path="/onboarding/school" element={
        <ProtectedRoute><SchoolOnboarding /></ProtectedRoute>
      } />

      {/* Authenticated Routes with Layout */}
      <Route path="/" element={
        <ProtectedRoute><AppLayout><Home /></AppLayout></ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute><AppLayout><Profile /></AppLayout></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute><AppLayout><StudentDashboard /></AppLayout></ProtectedRoute>
      } />
      <Route path="/create" element={
        <ProtectedRoute><AppLayout><CreatePost /></AppLayout></ProtectedRoute>
      } />
      <Route path="/feed" element={
        <ProtectedRoute><AppLayout><PostFeed /></AppLayout></ProtectedRoute>
      } />

      {/* Groups */}
      <Route path="/groups" element={
        <ProtectedRoute><AppLayout><Groups /></AppLayout></ProtectedRoute>
      } />
      <Route path="/groups/:id" element={
        <ProtectedRoute><AppLayout><GroupDetail /></AppLayout></ProtectedRoute>
      } />

      {/* Classes / Learning Spaces */}
      <Route path="/classes" element={
        <ProtectedRoute><AppLayout><Classes /></AppLayout></ProtectedRoute>
      } />
      <Route path="/classes/:id" element={
        <ProtectedRoute><AppLayout><ClassDetail /></AppLayout></ProtectedRoute>
      } />

      {/* School Dashboard */}
      <Route path="/school-dashboard" element={
        <ProtectedRoute><AppLayout><SchoolDashboard /></AppLayout></ProtectedRoute>
      } />

      {/* School ERP Routes */}

      <Route path="/erp" element={
        <ProtectedRoute><SchoolERPLayout /></ProtectedRoute>
      }>
        <Route index element={<ERPDashboard />} />
        <Route path="dashboard" element={<ERPDashboard />} />

        {/* Students */}
        <Route path="students" element={<StudentsList />} />
        <Route path="students/:id" element={<StudentProfile />} />
        <Route path="students/admission" element={<AdmissionWizard />} />
        <Route path="students/admissions" element={<Navigate to="/erp/students/admission" replace />} />
        <Route path="students/import" element={<BulkImport />} />
        <Route path="students/attendance" element={<StudentAttendance />} />

        {/* Academics */}
        <Route path="academics/classes" element={<ClassConfig />} />
        <Route path="academics/timetable" element={<TimetableBuilder />} />
        <Route path="academics/exams" element={<ExamManager />} />
        <Route path="academics/reports" element={<ReportCards />} />

        {/* Finance */}
        <Route path="finance/fees" element={<FeeManager />} />
        <Route path="finance/expenses" element={<ExpenseTracker />} />
        <Route path="finance/reports" element={<FinanceReports />} />

        {/* HR */}
        <Route path="hr/staff" element={<StaffDirectory />} />
        <Route path="hr/attendance" element={<StaffAttendance />} />
        <Route path="hr/payroll" element={<PayrollManager />} />
        <Route path="hr/leave" element={<LeaveManagement />} />

        {/* Library */}
        <Route path="library" element={<LibraryManager />} />

        {/* Transport */}
        <Route path="transport" element={<TransportManager />} />

        {/* Hostel */}
        <Route path="hostel" element={<HostelManager />} />

        {/* Inventory */}
        <Route path="inventory" element={<InventoryManager />} />

        {/* Front Office */}
        <Route path="reception" element={<FrontOffice />} />

        {/* Certificates */}
        <Route path="certificates" element={<CertificateManager />} />

        {/* Communication */}
        <Route path="communication" element={<CommunicationCenter />} />

        {/* Analytics */}
        <Route path="analytics" element={<AnalyticsDashboard />} />

        {/* Settings */}
        <Route path="settings" element={<SettingsManager />} />
      </Route>

      {/* Learning placeholder -> now redirect to classes */}
      <Route path="/learn" element={<Navigate to="/classes" />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Suspense fallback={<LoadingSpinner />}>
          <AppRoutes />
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
